import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { parametriaCamposTrabajadoresApi } from "../../../services/api/parametriaCamposTrabajadoresApi";
import { clienteApi } from "../../../services/api/clienteApi";
import "react-toastify/dist/ReactToastify.css";

const MantenimientoParametriaCamposTrabajadores = () => {
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  const [campos, setCampos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [campoToEdit, setCampoToEdit] = useState(null);
  const [formData, setFormData] = useState({
    nombre_campo: "",
    nombre_excel: "",
    nombre_display: "",
    activo: true,
    requerido: false,
    orden: 0,
    descripcion: "",
    tipo_campo: "texto",
    tabla_catalogo: "",
    campo_catalogo: "",
    proyecto_id: null,
    cliente_id: null,
  });
  const [filtro, setFiltro] = useState("");

  useEffect(() => {
    if (selectedProject?.id) {
      fetchClientes();
      // No cargar campos automáticamente, solo cuando se seleccione un cliente
      setCampos([]);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    // Solo cargar campos cuando se seleccione un cliente
    if (selectedProject?.id && clienteSeleccionado) {
      fetchCampos();
    } else if (selectedProject?.id && !clienteSeleccionado) {
      // Limpiar campos si no hay cliente seleccionado
      setCampos([]);
    }
  }, [clienteSeleccionado]);

  const fetchClientes = async () => {
    setLoadingClientes(true);
    try {
      const proyectoId = selectedProject?.id || null;
      if (proyectoId) {
        const response = await clienteApi.getByProyectoRest(proyectoId);
        // Manejar diferentes estructuras de respuesta
        let clientesData = [];
        if (Array.isArray(response.data)) {
          clientesData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          clientesData = response.data.data;
        }
        setClientes(clientesData);
      }
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || "Error al cargar los clientes";
      toast.error(errorMessage);
      // Si la tabla no existe, mostrar un mensaje más específico
      if (errorMessage.includes("doesn't exist")) {
        console.warn("⚠️ La tabla 'clientes' no existe en la base de datos. Contacte al administrador.");
      }
      setClientes([]); // Establecer array vacío para evitar errores
    } finally {
      setLoadingClientes(false);
    }
  };

  const fetchCampos = async () => {
    setLoading(true);
    try {
      const proyectoId = selectedProject?.id || null;
      const clienteId = clienteSeleccionado || null;
      const response = await parametriaCamposTrabajadoresApi.getAll(proyectoId, clienteId, null);
      setCampos(response.data || []);
    } catch (error) {
      console.error("Error al cargar campos:", error);
      toast.error("Error al cargar los campos de parametría");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setModalMode("create");
    setCampoToEdit(null);
    setFormData({
      nombre_campo: "",
      nombre_excel: "",
      nombre_display: "",
      activo: true,
      requerido: false,
      orden: campos.length + 1,
      descripcion: "",
      tipo_campo: "texto",
      tabla_catalogo: "",
      campo_catalogo: "",
      proyecto_id: selectedProject?.id || null,
      cliente_id: clienteSeleccionado || null,
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (campo) => {
    setModalMode("edit");
    setCampoToEdit(campo);
    setFormData({
      nombre_campo: campo.nombre_campo,
      nombre_excel: campo.nombre_excel,
      nombre_display: campo.nombre_display,
      activo: campo.activo,
      requerido: campo.requerido,
      orden: campo.orden,
      descripcion: campo.descripcion || "",
      tipo_campo: campo.tipo_campo || "texto",
      tabla_catalogo: campo.tabla_catalogo || "",
      campo_catalogo: campo.campo_catalogo || "",
      proyecto_id: campo.proyecto_id,
      cliente_id: campo.cliente_id,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCampoToEdit(null);
  };

  const handleSave = async () => {
    try {
      if (modalMode === "create") {
        await parametriaCamposTrabajadoresApi.create(formData);
        toast.success("Campo creado exitosamente");
      } else {
        await parametriaCamposTrabajadoresApi.update(campoToEdit.id, formData);
        toast.success("Campo actualizado exitosamente");
      }
      handleCloseModal();
      fetchCampos();
    } catch (error) {
      console.error("Error al guardar campo:", error);
      toast.error(error.response?.data?.detail || "Error al guardar el campo");
    }
  };

  const handleToggleActivo = async (campo) => {
    try {
      await parametriaCamposTrabajadoresApi.toggleActivo(campo.id, !campo.activo);
      toast.success(`Campo ${!campo.activo ? "activado" : "desactivado"} exitosamente`);
      fetchCampos();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      toast.error("Error al cambiar el estado del campo");
    }
  };

  const handleDelete = async (campo) => {
    if (window.confirm(`¿Está seguro de eliminar el campo "${campo.nombre_display}"?`)) {
      try {
        await parametriaCamposTrabajadoresApi.delete(campo.id);
        toast.success("Campo eliminado exitosamente");
        fetchCampos();
      } catch (error) {
        console.error("Error al eliminar campo:", error);
        toast.error(error.response?.data?.detail || "Error al eliminar el campo");
      }
    }
  };

  const camposFiltrados = campos.filter(
    (campo) =>
      campo.nombre_campo?.toLowerCase().includes(filtro.toLowerCase()) ||
      campo.nombre_excel?.toLowerCase().includes(filtro.toLowerCase()) ||
      campo.nombre_display?.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <>
      <ToastContainer />
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 8px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 8px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: #f9fafb !important;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <Container fluid>
          {/* Header */}
          <div className="dh-main-header">
            <Container>
              <h1 className="dh-main-title">Parametria Carga Masiva Trabajadores</h1>
              <p className="dh-main-subtitle">
                Configure los campos del Excel para la carga masiva de trabajadores filtrados por proyecto y cliente
              </p>
            </Container>
          </div>

          <Container>
            <Row>
              <Col>
                <Card className="dh-card">
                  <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
                    <span>Lista de Campos</span>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Select
                        value={clienteSeleccionado || ""}
                        onChange={(e) => setClienteSeleccionado(e.target.value ? parseInt(e.target.value) : null)}
                        className="dh-form-control"
                        style={{ width: "250px" }}
                        disabled={loadingClientes}
                        required
                      >
                        <option value="">Seleccione un cliente</option>
                        {clientes.map((cliente) => (
                          <option key={cliente.id} value={cliente.id}>
                            {cliente.nombre}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="text"
                        placeholder="Buscar campo..."
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        className="dh-form-control d-inline-block"
                        style={{ width: "300px" }}
                      />
                      <Button 
                        className="btn-dh-primary" 
                        onClick={handleOpenCreateModal}
                        disabled={!clienteSeleccionado}
                        title={!clienteSeleccionado ? "Debe seleccionar un cliente primero" : ""}
                      >
                        <FaPlus /> Nuevo Campo
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                      </div>
                    ) : (
                      <Table responsive className="dh-enhanced-table">
                        <thead>
                          <tr>
                            <th>Orden</th>
                            <th>Nombre Campo</th>
                            <th>Nombre Excel</th>
                            <th>Nombre Display</th>
                            <th>Cliente</th>
                            <th>Tipo</th>
                            <th>Activo</th>
                            <th>Requerido</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!clienteSeleccionado ? (
                            <tr>
                              <td colSpan="9" className="text-center py-5">
                                <div className="text-muted">
                                  <p className="mb-2">Por favor seleccione un cliente para ver los campos de parametría</p>
                                  <small>Los campos se mostrarán una vez que seleccione un cliente del dropdown</small>
                                </div>
                              </td>
                            </tr>
                          ) : camposFiltrados.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-5">
                                No hay campos configurados para este cliente
                              </td>
                            </tr>
                          ) : (
                            camposFiltrados.map((campo) => {
                              const clienteNombre = campo.cliente_id 
                                ? clientes.find(c => c.id === campo.cliente_id)?.nombre || `Cliente ID: ${campo.cliente_id}`
                                : "Global";
                              return (
                              <tr key={campo.id}>
                                <td>{campo.orden}</td>
                                <td>
                                  <code>{campo.nombre_campo}</code>
                                </td>
                                <td>{campo.nombre_excel}</td>
                                <td>{campo.nombre_display}</td>
                                <td>
                                  <Badge bg={campo.cliente_id ? "primary" : "secondary"}>
                                    {clienteNombre}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge bg={campo.tipo_campo === "catalogo" ? "info" : "secondary"}>
                                    {campo.tipo_campo}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    variant="link"
                                    className="p-0"
                                    onClick={() => handleToggleActivo(campo)}
                                    title={campo.activo ? "Desactivar" : "Activar"}
                                  >
                                    {campo.activo ? (
                                      <FaToggleOn size={24} color="#10b981" />
                                    ) : (
                                      <FaToggleOff size={24} color="#9ca3af" />
                                    )}
                                  </Button>
                                </td>
                                <td>
                                  {campo.requerido ? (
                                    <FaCheck color="#10b981" />
                                  ) : (
                                    <FaTimes color="#9ca3af" />
                                  )}
                                </td>
                                <td>
                                  <Button
                                    className="btn-dh-warning me-2"
                                    size="sm"
                                    onClick={() => handleOpenEditModal(campo)}
                                  >
                                    <FaEdit /> Editar
                                  </Button>
                                  {campo.proyecto_id === null && campo.cliente_id === null ? (
                                    <span className="text-muted" title="No se puede eliminar un campo global">
                                      <FaTrash className="text-muted" />
                                    </span>
                                  ) : (
                                    <Button
                                      className="btn-dh-danger"
                                      size="sm"
                                      onClick={() => handleDelete(campo)}
                                    >
                                      <FaTrash /> Eliminar
                                    </Button>
                                  )}
                                </td>
                              </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </Container>
      </div>

      {/* Modal para crear/editar */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" ? "Nuevo Campo" : "Editar Campo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    value={formData.cliente_id || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, cliente_id: e.target.value ? parseInt(e.target.value) : null })
                    }
                    className="dh-form-control"
                    disabled={modalMode === "create" && clienteSeleccionado !== null}
                  >
                    <option value="">Global (todos los clientes)</option>
                    {clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {modalMode === "create" && clienteSeleccionado !== null 
                      ? "El campo se creará para el cliente seleccionado. Puede cambiarlo al editar."
                      : "Seleccione un cliente para crear una parametría específica, o deje en blanco para una parametría global del proyecto"
                    }
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Campo (interno) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre_campo}
                    onChange={(e) =>
                      setFormData({ ...formData, nombre_campo: e.target.value })
                    }
                    className="dh-form-control"
                    disabled={modalMode === "edit"}
                    placeholder="ej: id_trabajador_externo"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Excel *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre_excel}
                    onChange={(e) =>
                      setFormData({ ...formData, nombre_excel: e.target.value })
                    }
                    className="dh-form-control"
                    placeholder="ej: ID TRABAJADOR"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Display *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre_display}
                    onChange={(e) =>
                      setFormData({ ...formData, nombre_display: e.target.value })
                    }
                    className="dh-form-control"
                    placeholder="ej: ID Trabajador"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Orden</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.orden}
                    onChange={(e) =>
                      setFormData({ ...formData, orden: parseInt(e.target.value) || 0 })
                    }
                    className="dh-form-control"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo Campo</Form.Label>
                  <Form.Select
                    value={formData.tipo_campo}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo_campo: e.target.value })
                    }
                    className="dh-form-control"
                  >
                    <option value="texto">Texto</option>
                    <option value="numero">Número</option>
                    <option value="fecha">Fecha</option>
                    <option value="catalogo">Catálogo</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.tipo_campo === "catalogo" && (
                <>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tabla Catálogo</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.tabla_catalogo}
                        onChange={(e) =>
                          setFormData({ ...formData, tabla_catalogo: e.target.value })
                        }
                        className="dh-form-control"
                        placeholder="ej: generos"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Campo Catálogo</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.campo_catalogo}
                        onChange={(e) =>
                          setFormData({ ...formData, campo_catalogo: e.target.value })
                        }
                        className="dh-form-control"
                        placeholder="ej: nombre"
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                className="dh-form-control"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Activo"
                  checked={formData.activo}
                  onChange={(e) =>
                    setFormData({ ...formData, activo: e.target.checked })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Requerido"
                  checked={formData.requerido}
                  onChange={(e) =>
                    setFormData({ ...formData, requerido: e.target.checked })
                  }
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button className="btn-dh-primary" onClick={handleSave}>
            {modalMode === "create" ? "Crear" : "Guardar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MantenimientoParametriaCamposTrabajadores;
